import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { 
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../../services/api';

const Settings = () => {
  const [settings, setSettings] = useState({});
  const [weekdays, setWeekdays] = useState([]);
  const [months, setMonths] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // États pour la régénération
  const [regenerating, setRegenerating] = useState(false);
  const [rangeDialogOpen, setRangeDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [regenerateResult, setRegenerateResult] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, weekdaysRes, monthsRes, languagesRes] = await Promise.all([
        api.get('/settings'),
        api.get('/settings/weekdays/all'),
        api.get('/settings/months/all'),
        api.get('/settings/languages/all')
      ]);
      
      setSettings(settingsRes.data.data);
      setWeekdays(weekdaysRes.data.data);
      setMonths(monthsRes.data.data);
      setLanguages(languagesRes.data.data);
    } catch (err) {
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/settings', settings);
      setSuccess('Paramètres enregistrés avec succès. Les nouveaux paramètres seront appliqués aux prochaines images générées.');
    } catch (err) {
      setError('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Régénérer l'image du jour
  const handleRegenerateToday = async () => {
    try {
      setRegenerating(true);
      setError('');
      const response = await api.post('/settings/regenerate/today');
      setSuccess(response.data.message);
      setRegenerateResult(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la régénération');
    } finally {
      setRegenerating(false);
    }
  };

  // Régénérer une plage de dates
  const handleRegenerateRange = async () => {
    try {
      setRegenerating(true);
      setError('');
      const response = await api.post('/settings/regenerate/range', {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD')
      });
      setSuccess(response.data.message);
      setRegenerateResult(response.data.data);
      setRangeDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la régénération');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Paramètres
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Paramètres généraux */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration générale
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Nom de l'application"
                  value={settings.app_name || ''}
                  onChange={(e) => handleChange('app_name', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Heure d'envoi par défaut"
                  value={settings.default_send_time || ''}
                  onChange={(e) => handleChange('default_send_time', e.target.value)}
                  fullWidth
                  placeholder="07:00"
                />
                <TextField
                  label="Titre de la section proverbe"
                  value={settings.proverb_title || ''}
                  onChange={(e) => handleChange('proverb_title', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Texte de pied de page"
                  value={settings.footer_text || ''}
                  onChange={(e) => handleChange('footer_text', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Signature de l'auteur"
                  value={settings.author_signature || ''}
                  onChange={(e) => handleChange('author_signature', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Année en cours"
                  value={settings.current_year || ''}
                  onChange={(e) => handleChange('current_year', e.target.value)}
                  fullWidth
                  type="number"
                />
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  Enregistrer
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Langues */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Langues disponibles
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Nom</TableCell>
                      <TableCell>Nom natif</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {languages.map(lang => (
                      <TableRow key={lang.id}>
                        <TableCell>{lang.code}</TableCell>
                        <TableCell>{lang.name}</TableCell>
                        <TableCell>{lang.native_name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={lang.is_active ? 'Actif' : 'Inactif'}
                            color={lang.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Jours de la semaine */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Jours de la semaine (Duala)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Français</TableCell>
                      <TableCell>Duala</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weekdays.map(day => (
                      <TableRow key={day.id}>
                        <TableCell>{day.french_name}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" sx={{ fontFamily: 'Palm Leaf, serif' }}>
                            {day.duala_name}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Mois de l'année */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Mois de l'année (Duala)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Français</TableCell>
                      <TableCell>Duala</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {months.map(month => (
                      <TableRow key={month.id}>
                        <TableCell>{month.french_name}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" sx={{ fontFamily: 'Palm Leaf, serif' }}>
                            {month.duala_name}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Régénération des images */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Régénération des images
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Utilisez ces options pour régénérer les images avec les nouveaux paramètres (titre, pied de page, signature).
                Les images déjà générées ne sont pas automatiquement mises à jour lors de la modification des paramètres.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={regenerating ? <CircularProgress size={20} color="inherit" /> : <TodayIcon />}
                  onClick={handleRegenerateToday}
                  disabled={regenerating}
                >
                  Régénérer l'image du jour
                </Button>
                
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DateRangeIcon />}
                  onClick={() => setRangeDialogOpen(true)}
                  disabled={regenerating}
                >
                  Régénérer une plage de dates
                </Button>
              </Box>
              
              {regenerateResult && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Résultat de la régénération :</Typography>
                  {regenerateResult.total ? (
                    <Typography variant="body2">
                      Total: {regenerateResult.total} | Réussis: {regenerateResult.success} | Échecs: {regenerateResult.failed}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      Date: {regenerateResult.date} | ID: {regenerateResult.id}
                    </Typography>
                  )}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog pour la plage de dates */}
      <Dialog open={rangeDialogOpen} onClose={() => setRangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateRangeIcon />
            Régénérer une plage de dates
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sélectionnez la période pour laquelle vous souhaitez régénérer les images.
            Attention : cela peut prendre du temps pour de longues périodes.
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <DatePicker
                label="Date de début"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="Date de fin"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRangeDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={handleRegenerateRange}
            variant="contained"
            color="primary"
            disabled={regenerating}
            startIcon={regenerating ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {regenerating ? 'Régénération...' : 'Régénérer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
